import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api, timeAgo } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Send } from 'lucide-react';

export default function ChatRoom() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thread, setThread] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    loadMessages();
    connectWebSocket();
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [threadId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    try {
      const [msgsRes, threadsRes] = await Promise.all([
        api.get(`/messages/threads/${threadId}`),
        api.get('/messages/threads'),
      ]);
      setMessages(msgsRes.data);
      const t = threadsRes.data.find(th => th.thread_id === threadId);
      setThread(t);
    } catch {
      navigate('/messages');
    }
  };

  const connectWebSocket = () => {
    if (!user?.user_id) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/${user.user_id}`);
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'message' && data.thread_id === threadId) {
          setMessages(prev => [...prev, data.message]);
        }
      } catch { /* ignore */ }
    };
    ws.onclose = () => {
      setTimeout(connectWebSocket, 3000);
    };
    wsRef.current = ws;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const otherId = thread?.participants?.find(p => p !== user?.user_id);
    if (!otherId) return;
    setSending(true);
    try {
      const res = await api.post('/messages', { receiver_id: otherId, content: input.trim() });
      setMessages(prev => [...prev, res.data.message]);
      setInput('');
    } catch { /* ignore */ }
    setSending(false);
  };

  const otherName = thread?.participant_names?.[thread?.participants?.find(p => p !== user?.user_id)] || 'Chat';

  return (
    <div className="flex flex-col h-screen max-w-lg mx-auto bg-[#FDFBF7]" data-testid="chat-room">
      {/* Header */}
      <div className="glass sticky top-0 z-10 px-4 py-3 flex items-center gap-3 border-b-2 border-gray-100">
        <button onClick={() => navigate('/messages')} className="text-[#5C6B7F]" data-testid="chat-back">
          <ArrowLeft size={24} />
        </button>
        <div className="w-10 h-10 rounded-full bg-[#6C63FF] flex items-center justify-center text-white font-bold">
          {otherName?.[0]}
        </div>
        <h2 className="text-lg font-bold text-[#011627]">{otherName}</h2>
      </div>

      {/* Messages */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 no-scrollbar" style={{ paddingBottom: '140px' }}>
        {messages.map((msg, i) => {
          const isMine = msg.sender_id === user?.user_id;
          return (
            <div key={msg.msg_id || i} className={`flex ${isMine ? 'justify-end' : 'justify-start'} animate-fade-in`}>
              <div className={`max-w-[80%] px-4 py-3 ${
                isMine
                  ? 'bg-[#2EC4B6] text-white chat-bubble-sent'
                  : 'bg-white border-2 border-gray-100 text-[#011627] chat-bubble-received'
              }`}>
                <p className="text-sm font-semibold leading-relaxed">{msg.content}</p>
                <p className={`text-[10px] mt-1 ${isMine ? 'text-white/70' : 'text-[#9AA5B1]'}`}>
                  {timeAgo(msg.created_at)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="fixed bottom-20 left-0 right-0 max-w-lg mx-auto px-4 py-3 glass border-t-2 border-gray-100">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="Type a message..."
            className="flex-1 bg-white border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
            data-testid="chat-input"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className="rounded-2xl bg-[#2EC4B6] hover:bg-[#25A296] text-white px-4 btn-bounce"
            data-testid="chat-send"
          >
            <Send size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
}
